<script lang="ts">
import { useTurnstileSlot } from '@/composables/useTurnstileSlot'
import type { NullableError, User } from '@/types'
import { isE164PhoneNumber, isPassword } from '@/utils/input-validator-utils'
import { formatToPhoneNumber } from '@/utils/utils'
import { ThemeType } from '@goodlifeplus/themes'
import { defineComponent } from 'vue'

import ForgotPasswordDrawer from '../drawers/ForgotPasswordDrawer.vue'
import RadioInput from '../inputs/RadioInput.vue'

let registerSigninFormCount = 0

/**
 * RegisterSignInForm
 */
export default defineComponent({
  components: {
    ForgotPasswordDrawer,
    RadioInput,
  },
  props: {
    selectedAction: {
      type: String,
      default: 'register',
    },
  },
  emits: [
    'isLoading',
    'openForgotPasswordDrawer',
    'submitLogin',
    'submitRegister',
    'update:selectedAction',
  ],
  setup() {
    return useTurnstileSlot()
  },
  data() {
    return {
      form: {
        isLoading: false,
        selectedAction: this.selectedAction,
        rememberMe: true,
        data: {
          ...this.$userStore.newEmptyUser.value,
          email: this.$userStore.user?.email ?? '',
          password: '',
        } as User,
        errors: null as NullableError,
        validators: {},
      },
      openModalFunction: null as (() => void) | null,
    }
  },
  computed: {
    id(): string {
      return `${this.$attrs.id ?? 'signup-signin-modal'}-${++registerSigninFormCount}-form`
    },
    themeNameDisplay(): string {
      const name = this.$theme.name

      if (this.$theme.type === ThemeType.Club) {
        return `${name} Prizes`
      }

      return this.BUSINESS_NAME
    },
  },
  watch: {
    selectedAction(val: string) {
      this.form.selectedAction = val
    },
    'form.selectedAction'(val: string) {
      this.$emit('update:selectedAction', val)

      if (val === 'login') {
        this.form.data.first_name = ''
        this.form.data.last_name = ''
        this.form.data.phone = ''
        this.form.rememberMe = true
        this.form.errors = null
        this.form.validators = {
          password: () => true,
          phone: [() => true],
        }
      } else {
        this.form.validators = {
          password: isPassword,
          phone: [isE164PhoneNumber],
        }
      }
    },
    isLoading(val: boolean) {
      if (val) {
        this.$emit('isLoading', true)
      } else {
        this.$emit('isLoading', false)
      }
    },
  },
  methods: {
    async submit(isValid: boolean) {
      if (!isValid) {
        return
      }

      this.form.errors = null

      if (this.form.selectedAction === 'login') {
        await this.handleLogin()
      } else {
        await this.handleRegister()
      }
    },
    async handleLogin() {
      this.form.isLoading = true

      try {
        await this.withTurnstileSlot(() =>
          this.$userStore.logIn(
            this.form.data.email,
            this.form.data.password,
            this.form.rememberMe
          )
        )

        this.$emit('submitLogin', true)
      } catch (e: any) {
        this.form.errors = e
      } finally {
        this.form.isLoading = false
      }
    },
    async handleRegister() {
      this.form.isLoading = true

      try {
        await this.withTurnstileSlot(() =>
          this.$userStore.register({
            email: this.form.data.email,
            password: this.form.data.password,
            first_name: this.form.data.first_name,
            last_name: this.form.data.last_name,
            phone: `${this.form.data.phone}`,
          })
        )

        this.$emit('submitRegister', true)
      } catch (e: any) {
        this.form.errors = e
      } finally {
        this.form.isLoading = false
      }
    },
    openModalWithSelectedAction(selectedAction: string, openModal: () => void) {
      this.form.selectedAction = selectedAction
      openModal()
    },
    openForgotPasswordDrawer(openDrawer: () => void) {
      openDrawer()
      this.$emit('openForgotPasswordDrawer')
    },
    formatPhoneNumber() {
      this.form.data.phone = formatToPhoneNumber(this.form.data.phone)
    },
  },
})
</script>

<template>
  <AppForm
    :id="id"
    :data="form.data"
    :errors="form.errors"
    class="flex flex-col w-full"
    :show-required-note="false"
    :show-complete-errors="false"
    :validators="form.selectedAction === 'register' ? form.validators : null"
    @submit="submit"
  >
    <template #default="{ errors }">
      <div class="mt-4 text-center text-xl font-bold">Welcome</div>

      <fieldset>
        <div class="flex h-full w-full p-6 lg:w-[490px]">
          <div class="flex w-full flex-col rounded-lg border">
            <div class="flex w-full flex-col">
              <RadioInput
                v-model="form.selectedAction"
                name="action"
                value="login"
                :disabled="form.isLoading"
                class="rounded-t-lg px-5 py-2"
                :class="{
                  'bg-slate-300 px-2': form.selectedAction === 'login',
                }"
              >
                <div class="ml-2 flex flex-wrap items-baseline gap-1 font-bold">
                  Sign in
                  <span class="text-sm font-normal">Already a customer?</span>
                </div>
              </RadioInput>

              <RadioInput
                v-model="form.selectedAction"
                name="action"
                value="register"
                :disabled="form.isLoading"
                class="px-5 py-2"
                :class="{
                  'bg-slate-300 px-2': form.selectedAction === 'register',
                }"
              >
                <div class="ml-2 flex flex-wrap items-baseline gap-1 font-bold">
                  Create account
                  <span class="text-sm font-normal capitalize"
                    >New to {{ themeNameDisplay }}?</span
                  >
                </div>
              </RadioInput>
            </div>
            <div class="mt-4 px-5">
              <AppInput
                v-if="form.selectedAction === 'register'"
                :id="`${id ?? 'signup-signin-modal'}-form-first-name`"
                v-model="form.data.first_name"
                label="First Name"
                :use-floating-placeholder="false"
                :disabled="form.isLoading"
                :errors
                :required="true"
                autocomplete="given-name"
                placeholder="First name"
                name="first_name"
                data-cy="first-name-input"
              />
            </div>

            <div class="px-5">
              <AppInput
                v-if="form.selectedAction === 'register'"
                :id="`${id ?? 'signup-signin-modal'}-form-last-name`"
                v-model="form.data.last_name"
                label="Last Name"
                :use-floating-placeholder="false"
                :disabled="form.isLoading"
                :errors
                :required="true"
                autocomplete="family-name"
                placeholder="Last name"
                name="last_name"
                data-cy="last-name-input"
              />
            </div>

            <div class="px-5">
              <AppInput
                :id="`${id ?? 'signup-signin-modal'}-form-email`"
                v-model="form.data.email"
                label="Email Address"
                :use-floating-placeholder="false"
                :disabled="form.isLoading"
                :errors
                :required="true"
                placeholder="Email"
                name="email"
                type="email"
                data-cy="email-input"
              />
            </div>

            <div class="px-5">
              <AppInput
                v-if="form.selectedAction === 'register'"
                :id="`${id ?? 'signup-signin-modal'}-form-phone`"
                v-model="form.data.phone"
                label="Phone"
                :use-floating-placeholder="false"
                :disabled="form.isLoading"
                :errors
                :required="true"
                placeholder="Phone"
                name="phone"
                type="phone"
                data-cy="phone-input"
                @change="formatPhoneNumber"
              />
            </div>

            <div class="px-5">
              <AppInput
                :id="`${id ?? 'signup-signin-modal'}-form-password`"
                v-model="form.data.password"
                label="Password"
                :use-floating-placeholder="false"
                :disabled="form.isLoading"
                :errors
                :required="true"
                placeholder="Password"
                name="password"
                type="password"
                data-cy="password-input"
                :hint="
                  form.selectedAction === 'register'
                    ? 'Must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one symbol'
                    : ''
                "
              />
            </div>

            <div class="flex justify-between px-5 text-sm">
              <AppToggle
                v-model="form.rememberMe"
                name="remember-me"
                class="flex-shrink-0"
                checked-background="bg-primary"
              >
                Remember Me
              </AppToggle>
              <ForgotPasswordDrawer>
                <template #button="{ openDrawer }">
                  <button
                    type="button"
                    class="text-right font-bold underline hover:cursor-pointer hover:no-underline"
                    @click="openForgotPasswordDrawer(openDrawer)"
                  >
                    Forgot password?
                  </button>
                </template>
              </ForgotPasswordDrawer>
            </div>

            <p
              v-if="errors?.message"
              class="form-error my-1 text-center text-xs text-red-500"
              data-cy="validation-error"
              v-html="errors.message"
            />

            <div ref="turnstileSlot" class="mt-3 px-5 empty:mt-0" />

            <div class="px-5 pb-4 grid w-full mt-4">
              <AppTextButton
                :id="`${id ?? 'signup-signin-modal'}-form-continue`"
                :size="UI.TextButton.Size.Large"
                :variant="UI.TextButton.Variant.Primary"
                :icon="UI.Icon.Name.ArrowRight"
                :busy="form.isLoading"
                label="Continue"
                data-cy="submit-registration-form"
              />
            </div>
          </div>
        </div>
      </fieldset>
    </template>
  </AppForm>
</template>

<style lang="scss" scoped></style>
